'use client';

import { cn } from '@core/utils';
import { createContext, useEffect, useRef, useState } from 'react';

import type { HTMLTagProps } from '../../../types';
import styles from './InputDecorator.module.scss';

interface InputDecoratorProps extends HTMLTagProps<'div'> {
  label?: React.ReactNode;
  required?: boolean;
  validationMessage?: string;
  message?: string;
  htmlFor?: string;
  tooltip?: React.ReactNode;
  labelClassName?: string;
  messageClassName?: string;
}

type InputDecoratorContextValue = Pick<
  InputDecoratorProps,
  'label' | 'validationMessage'
>;

export const InputDecoratorContext =
  createContext<InputDecoratorContextValue | null>(null);

export const InputDecorator = ({
  label,
  required,
  validationMessage,
  message,
  className,
  labelClassName,
  messageClassName,
  children,
  htmlFor,
  tooltip,
  ...restProps
}: InputDecoratorProps) => {
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const hasMessage = !!(validationMessage || message);

  const [messageWrapHeight, setMessageWrapHeight] =
    useState<React.CSSProperties>();

  useEffect(() => {
    const { current } = messageContainerRef;
    if (current && hasMessage) {
      return setMessageWrapHeight({
        height: current.clientHeight,
      });
    }

    return setMessageWrapHeight(undefined);
  }, [hasMessage]);

  return (
    <div {...restProps} className={cn('flex flex-col', className)}>
      {label || tooltip ? (
        <div className="mb-8 flex items-center gap-4">
          {label ? (
            <label
              className={cn(
                'w-full text-start font-400 text-Gray-800 typo-body4',
                {
                  "after:content-['*'] after:text-R-500 after:ml-1": !!required,
                },
                labelClassName,
              )}
              htmlFor={
                htmlFor ?? (typeof label === 'string' ? label : undefined)
              }
            >
              {label}
            </label>
          ) : null}
          {tooltip}
        </div>
      ) : null}
      <InputDecoratorContext.Provider value={{ label, validationMessage }}>
        {children}
      </InputDecoratorContext.Provider>
      {hasMessage ? (
        <div
          className={cn('mt-0 h-0 overflow-hidden transition-size-location', {
            'mt-4': hasMessage,
          })}
          style={messageWrapHeight}
        >
          <div
            ref={messageContainerRef}
            className="mx-5 flex flex-col gap-8 font-400 typo-caption1"
          >
            {validationMessage ? (
              <span className={styles['validation-message']}>
                {validationMessage}
              </span>
            ) : null}

            {message ? (
              <span className={cn('text-Gray-600', messageClassName)}>
                {message}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
